import { useState } from 'react';
import { Menu, X, Home, Pill, Activity, FlaskConical, Calendar, MessageSquare, User, Bell, LogOut, Heart, TrendingUp, FileText, Upload, Wind } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';

interface PatientTopNavProps {
  patientName: string;
  adherenceScore?: number;
  currentView?: string;
  onNavigate: (view: string) => void;
  onLogout?: () => void;
  notificationCount?: number;
}

export function PatientTopNav({
  patientName,
  adherenceScore = 0,
  currentView = 'dashboard',
  onNavigate,
  onLogout,
  notificationCount = 0,
}: PatientTopNavProps) {
  const [isOpen, setIsOpen] = useState(false);

  const quickActions = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: Home,
      description: 'View your health overview',
      color: 'text-[#2762AC]',
    },
    {
      id: 'vitals',
      label: 'Log Vitals',
      icon: Activity,
      description: 'Record your vital signs',
      color: 'text-[#F07D13]',
    },
    {
      id: 'medication-log',
      label: 'Medications',
      icon: Pill,
      description: 'Log your medications',
      color: 'text-[#14CAF4]',
    },
    {
      id: 'library',
      label: 'Exercise Library',
      icon: Wind,
      description: 'Browse exercises',
      color: 'text-[#2762AC]',
    },
    {
      id: 'hometest-log',
      label: 'Home Tests',
      icon: FlaskConical,
      description: 'Record test results',
      color: 'text-[#F07D13]',
    },
    {
      id: 'appointments',
      label: 'Appointments',
      icon: Calendar,
      description: 'Manage appointments',
      color: 'text-[#14CAF4]',
    },
    {
      id: 'messaging',
      label: 'Messages',
      icon: MessageSquare,
      description: 'Chat with care team',
      color: 'text-[#2762AC]',
    },
    {
      id: 'symptoms',
      label: 'Log Symptoms',
      icon: Heart,
      description: 'Track your symptoms',
      color: 'text-[#F07D13]',
    },
    {
      id: 'labreport',
      label: 'Upload Lab Report',
      icon: Upload,
      description: 'Upload test reports',
      color: 'text-[#14CAF4]',
    },
    {
      id: 'discharge-summary',
      label: 'Discharge Summary',
      icon: FileText,
      description: 'View discharge info',
      color: 'text-[#2762AC]',
    },
  ];

  const handleNavigation = (view: any) => {
    onNavigate(view);
    setIsOpen(false);
  };

  return (
    <div className="sticky top-0 z-50 w-full bg-white border-b border-gray-200 shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 max-w-7xl mx-auto">
        {/* Left: Logo + Menu */}
        <div className="flex items-center gap-3">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden">
                <Menu className="size-6 text-[#2762AC]" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] sm:w-[350px] p-0">
              <SheetDescription className="sr-only">
                Navigation menu for accessing your health dashboard, medications, exercises, tests, appointments, and messages
              </SheetDescription>
              <SheetHeader className="p-6 pb-4 bg-gradient-to-r from-[#2762AC] to-[#14CAF4]">
                <div className="flex items-center gap-3">
                  <Avatar className="size-12 border-2 border-white">
                    <AvatarImage src="" />
                    <AvatarFallback className="bg-white text-[#2762AC]">
                      {patientName.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <SheetTitle className="text-white">{patientName}</SheetTitle>
                    <div className="flex items-center gap-2 mt-1">
                      <Heart className="size-3 text-white" />
                      <span className="text-white text-xs">Adherence: {adherenceScore}%</span>
                    </div>
                  </div>
                </div>
              </SheetHeader>

              <div className="px-4 py-6 space-y-2">
                {/* Quick Actions */}
                <div className="space-y-1">
                  {quickActions.map((action) => {
                    const Icon = action.icon;
                    const isActive = currentView === action.id;
                    
                    return (
                      <button
                        key={action.id}
                        onClick={() => handleNavigation(action.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                          isActive
                            ? 'bg-[#2762AC]/10 border-l-4 border-[#2762AC]'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <Icon className={`size-5 ${isActive ? 'text-[#2762AC]' : action.color}`} />
                        <div className="flex-1 text-left">
                          <div className={`${isActive ? 'text-[#2762AC]' : 'text-gray-900'}`}>
                            {action.label}
                          </div>
                          <div className="text-xs text-gray-500">{action.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <Separator className="my-4" />

                {/* Profile & Settings */}
                <button
                  onClick={() => handleNavigation('profile')}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-all"
                >
                  <User className="size-5 text-gray-600" />
                  <div className="flex-1 text-left">
                    <div className="text-gray-900">Profile & Settings</div>
                    <div className="text-xs text-gray-500">Manage your account</div>
                  </div>
                </button>

                {/* Notifications */}
                <button
                  onClick={() => handleNavigation('notifications')}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-all"
                >
                  <Bell className="size-5 text-gray-600" />
                  <div className="flex-1 text-left">
                    <div className="text-gray-900 flex items-center gap-2">
                      Notifications
                      {notificationCount > 0 && (
                        <Badge className="bg-[#F07D13] text-white text-xs px-1.5 py-0">
                          {notificationCount}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">View updates</div>
                  </div>
                </button>

                <Separator className="my-4" />

                {/* Logout */}
                {onLogout && (
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      onLogout();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-50 text-red-600 transition-all"
                  >
                    <LogOut className="size-5" />
                    <div className="flex-1 text-left">
                      <div>Log Out</div>
                    </div>
                  </button>
                )}
              </div>
            </SheetContent>
          </Sheet>

          {/* Removed duplicate logo - "Pulmonology Spectrum by Healthkon" is already shown in the main header */}
        </div>

        {/* Center: Page Title (hidden on mobile) */}
        <div className="hidden md:block">
          <h1 className="text-[#2762AC] capitalize">
            {currentView === 'dashboard' ? 'My Health Dashboard' : currentView.replace('-', ' ')}
          </h1>
        </div>

        {/* Right: Desktop Quick Actions */}
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onNavigate('notifications')}
            className="relative hidden sm:flex"
          >
            <Bell className="size-5 text-[#2762AC]" />
            {notificationCount > 0 && (
              <Badge className="absolute -top-1 -right-1 size-5 flex items-center justify-center p-0 bg-[#F07D13] text-white text-xs">
                {notificationCount}
              </Badge>
            )}
          </Button>

          {/* Profile */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onNavigate('profile')}
            className="hidden sm:flex"
          >
            <Avatar className="size-8">
              <AvatarImage src="" />
              <AvatarFallback className="bg-[#2762AC] text-white text-xs">
                {patientName.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
          </Button>

          {/* Mobile Notifications */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onNavigate('notifications')}
            className="relative sm:hidden"
          >
            <Bell className="size-5 text-[#2762AC]" />
            {notificationCount > 0 && (
              <Badge className="absolute -top-1 -right-1 size-4 flex items-center justify-center p-0 bg-[#F07D13] text-white text-[10px]">
                {notificationCount}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Desktop Quick Actions Bar - Below Main Nav */}
      <div className="hidden lg:block border-t border-gray-100 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center gap-2 overflow-x-auto">
            {quickActions.map((action) => {
              const Icon = action.icon;
              const isActive = currentView === action.id;
              
              return (
                <Button
                  key={action.id}
                  onClick={() => handleNavigation(action.id)}
                  variant="ghost"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-[#2762AC] text-white hover:bg-[#2762AC]/90'
                      : 'hover:bg-[#2762AC]'
                  }`}
                >
                  <Icon className="size-4" />
                  <span className="text-sm">{action.label}</span>
                </Button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}